import { useState } from "react";
import {
  CheckCircle2,
  Edit3,
  Gift,
  Plus,
  Send,
  Settings,
  Trash2,
  XCircle,
  AlertCircle,
  HelpCircle,
  Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useLanguage } from "@/lib/i18n";

interface WelfareTabProps {
  idToken: string | null;
  isManager: boolean;
}

export interface WelfareTypeItem {
  id: number;
  titleTh: string;
  titleMy?: string | null;
  maxAmount: number;
  description?: string | null;
  isActive: number;
}

export function WelfareTab({ idToken, isManager }: WelfareTabProps) {
  const { language } = useLanguage();
  // The admin portal is always Thai. Employee pages follow the registered language.
  const isMy = !isManager && language === "my";

  // Modals state
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [editingType, setEditingType] = useState<WelfareTypeItem | null>(null);

  // Claim Form state
  const [selectedTypeId, setSelectedTypeId] = useState<number | "">("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");

  // Admin Type Form state
  const [typeForm, setTypeForm] = useState({
    titleTh: "",
    titleMy: "",
    maxAmount: "1000",
    description: "",
  });

  // Queries
  const typesQuery = trpc.welfare.types.useQuery(
    { idToken: idToken || "", all: isManager },
    { staleTime: 10000, enabled: Boolean(idToken) }
  );

  const welfareQuery = trpc.welfare.list.useQuery(
    { idToken: idToken || "" },
    { enabled: Boolean(idToken) }
  );

  // Mutations
  const requestWelfare = trpc.welfare.request.useMutation({
    onSuccess: () => {
      toast.success(isMy ? "သက်သာချောင်ချိရေး လျှောက်ထားပြီးပါပြီ" : "ยื่นคำขอสวัสดิการเรียบร้อยแล้ว");
      setShowClaimModal(false);
      setAmount("");
      setReason("");
      setSelectedTypeId("");
      welfareQuery.refetch();
    },
    onError: (err) => {
      toast.error(err.message || "ไม่สามารถยื่นคำขอสวัสดิการได้");
    },
  });

  const decideWelfare = trpc.welfare.decide.useMutation({
    onSuccess: (data, variables) => {
      const msg =
        variables.status === "approved"
          ? "อนุมัติสวัสดิการสำเร็จ พร้อมแจ้งเตือน LINE ให้พนักงานแล้ว!"
          : "ปฏิเสธคำขอสวัสดิการเรียบร้อยแล้ว";
      toast.success(msg);
      welfareQuery.refetch();
    },
    onError: (err) => {
      toast.error(err.message || "เกิดข้อผิดพลาดในการพิจารณา");
    },
  });

  const createType = trpc.welfare.createType.useMutation({
    onSuccess: () => {
      toast.success("เพิ่มประเภทสวัสดิการใหม่เรียบร้อยแล้ว");
      setShowTypeModal(false);
      resetTypeForm();
      typesQuery.refetch();
    },
    onError: (err) => {
      toast.error(err.message || "ไม่สามารถเพิ่มประเภทสวัสดิการได้");
    },
  });

  const updateType = trpc.welfare.updateType.useMutation({
    onSuccess: () => {
      toast.success("บันทึกการแก้ไขประเภทสวัสดิการเรียบร้อยแล้ว");
      setShowTypeModal(false);
      resetTypeForm();
      typesQuery.refetch();
    },
    onError: (err) => {
      toast.error(err.message || "ไม่สามารถแก้ไขประเภทสวัสดิการได้");
    },
  });

  const deleteType = trpc.welfare.deleteType.useMutation({
    onSuccess: () => {
      toast.success("ลบประเภทสวัสดิการเรียบร้อยแล้ว");
      typesQuery.refetch();
    },
    onError: (err) => {
      toast.error(err.message || "ไม่สามารถลบประเภทสวัสดิการได้");
    },
  });

  const resetTypeForm = () => {
    setEditingType(null);
    setTypeForm({
      titleTh: "",
      titleMy: "",
      maxAmount: "1000",
      description: "",
    });
  };

  const openAddTypeModal = () => {
    resetTypeForm();
    setShowTypeModal(true);
  };

  const openEditTypeModal = (item: WelfareTypeItem) => {
    setEditingType(item);
    setTypeForm({
      titleTh: item.titleTh,
      titleMy: item.titleMy || "",
      maxAmount: item.maxAmount ? String(item.maxAmount) : "0",
      description: item.description || "",
    });
    setShowTypeModal(true);
  };

  const handleSaveType = (e: React.FormEvent) => {
    e.preventDefault();
    if (!idToken) return;
    const titleTh = typeForm.titleTh.trim();
    if (!titleTh) {
      toast.error("กรุณาระบุชื่อสวัสดิการ (ไทย)");
      return;
    }
    const maxAmt = Number(typeForm.maxAmount) || 0;

    if (editingType) {
      updateType.mutate({
        idToken,
        id: editingType.id,
        titleTh,
        titleMy: typeForm.titleMy.trim() || null,
        maxAmount: maxAmt,
        description: typeForm.description.trim() || null,
      });
    } else {
      createType.mutate({
        idToken,
        titleTh,
        titleMy: typeForm.titleMy.trim() || undefined,
        maxAmount: maxAmt,
        description: typeForm.description.trim() || undefined,
      });
    }
  };

  const handleDeleteType = (id: number, title: string) => {
    if (!idToken) return;
    if (window.confirm(`ยืนยันการลบประเภทสวัสดิการ "${title}" หรือไม่?`)) {
      deleteType.mutate({ idToken, id });
    }
  };

  const handleToggleTypeActive = (item: WelfareTypeItem) => {
    if (!idToken) return;
    updateType.mutate({
      idToken,
      id: item.id,
      isActive: item.isActive ? 0 : 1,
    });
  };

  const availableTypes: WelfareTypeItem[] = typesQuery.data?.types || [];
  const selectedTypeItem = availableTypes.find((t) => t.id === Number(selectedTypeId));

  const handleClaimSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!idToken) return;
    if (!selectedTypeItem) {
      toast.error(isMy ? "သက်သာချောင်ချိရေး အမျိုးအစား ရွေးချယ်ပါ" : "กรุณาเลือกประเภทสวัสดิการ");
      return;
    }
    const num = Number(amount);
    if (!num || num <= 0) {
      toast.error(isMy ? "မှန်ကန်သော ငွေပမာဏ ထည့်ပါ" : "กรุณาระบุจำนวนเงินที่ถูกต้อง");
      return;
    }
    if (selectedTypeItem.maxAmount > 0 && num > selectedTypeItem.maxAmount) {
      toast.error(
        isMy
          ? `ငွေပမာဏသည် သတ်မှတ်ထားသော အများဆုံး ${selectedTypeItem.maxAmount.toLocaleString()} ဘတ် ထက်မကျော်ရပါ`
          : `จำนวนเงินต้องไม่เกินวงเงินสูงสุดที่กำหนดไว้ ${selectedTypeItem.maxAmount.toLocaleString()} บาท`
      );
      return;
    }

    const typeTitle = isMy && selectedTypeItem.titleMy ? selectedTypeItem.titleMy : selectedTypeItem.titleTh;

    requestWelfare.mutate({
      idToken,
      welfareTypeId: selectedTypeItem.id,
      welfareType: typeTitle,
      amount: num,
      reason: reason.trim() || undefined,
    });
  };

  const claims = welfareQuery.data?.claims || [];

  return (
    <div className="employee-module welfare-module space-y-3 pb-4">
      {/* Header Compact Card */}
      <Card className="glass-panel rounded-2xl border-white/80 shadow-xs">
        <CardHeader className="p-3.5 sm:p-4 pb-3">
          <div className="flex flex-wrap items-center justify-between gap-2.5">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-tr from-amber-500 to-rose-500 text-white shadow-xs">
                <Gift size={18} />
              </div>
              <div>
                <CardTitle className="text-base sm:text-lg font-black text-slate-900 leading-tight">
                  {isMy ? "သက်သာချောင်ချိရေး (Welfare)" : "สวัสดิการพนักงาน (Welfare)"}
                </CardTitle>
                <CardDescription className="text-sm text-slate-600 line-clamp-2">
                  {isManager
                    ? "จัดการประเภทสวัสดิการ กำหนดวงเงิน และพิจารณาอนุมัติคำขอ"
                    : isMy
                    ? "သက်သာချောင်ချိရေး လျှောက်ထားရန်နှင့် အခြေအနေ စစ်ဆေးရန်"
                    : "เบิกสวัสดิการตามสิทธิ์ที่บริษัทกำหนด"}
                </CardDescription>
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              {isManager && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={openAddTypeModal}
                  className="rounded-xl font-bold text-xs border-amber-300 text-amber-900 bg-amber-50/70 hover:bg-amber-100 h-8 px-2.5"
                >
                  <Plus size={14} className="mr-1" />
                  + เพิ่มสวัสดิการ
                </Button>
              )}

              {!isManager && (
                <Button
                  size="sm"
                  onClick={() => {
                    if (availableTypes.length > 0 && selectedTypeId === "") {
                      setSelectedTypeId(availableTypes[0].id);
                    }
                    setShowClaimModal(true);
                  }}
                  className="rounded-xl font-bold text-xs bg-amber-600 hover:bg-amber-700 text-white shadow-xs h-8 px-3"
                >
                  <Plus size={14} className="mr-1" />
                  {isMy ? "လျှောက်ထားမည်" : "ยื่นขอสวัสดิการ"}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Admin Welfare Management Section */}
      {isManager && (
        <Card className="glass-panel rounded-2xl border-amber-200/60 bg-amber-50/20 shadow-xs">
          <CardHeader className="p-3 sm:p-4 pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <Settings size={15} className="text-amber-700" />
                <h3 className="text-sm sm:text-base font-black text-slate-900">
                  ประเภทสวัสดิการที่เปิดให้พนักงานขอเบิก ({availableTypes.length})
                </h3>
              </div>
              <span className="hidden sm:inline text-sm text-slate-600">
                แอดมินกำหนดเองได้ทั้งหมด
              </span>
            </div>
          </CardHeader>
          <CardContent className="p-3 sm:p-4 pt-1">
            <div className="grid gap-2 sm:grid-cols-2">
              {availableTypes.map((item) => (
                <div
                  key={item.id}
                  className={`p-2.5 rounded-xl border transition-all flex flex-col justify-between ${
                    item.isActive
                      ? "bg-white border-slate-200"
                      : "bg-slate-100/70 border-dashed border-slate-300 opacity-60"
                  }`}
                >
                  <div>
                    <div className="flex items-start justify-between gap-1.5">
                      <div className="min-w-0">
                        <h4 className="text-sm font-black text-slate-900 line-clamp-2">
                          {item.titleTh}
                        </h4>
                      </div>
                      <Badge
                        variant={item.isActive ? "default" : "secondary"}
                        className="text-xs font-bold shrink-0 py-1 h-auto"
                      >
                        {item.isActive ? "เปิดใช้งาน" : "ปิดใช้งาน"}
                      </Badge>
                    </div>

                    <div className="mt-2 flex items-center gap-2 text-sm">
                      <span className="font-bold text-emerald-800 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">
                        {item.maxAmount > 0
                          ? `สูงสุด ${item.maxAmount.toLocaleString()} ฿`
                          : "ไม่จำกัดวงเงิน"}
                      </span>
                    </div>

                    {item.description && (
                      <p className="text-sm text-slate-600 mt-2 line-clamp-2">
                        {item.description}
                      </p>
                    )}
                  </div>

                  <div className="mt-2 pt-1.5 border-t border-slate-100 flex items-center justify-between text-xs">
                    <button
                      type="button"
                      onClick={() => handleToggleTypeActive(item)}
                      className="text-sm font-bold text-slate-600 hover:text-slate-900"
                    >
                      {item.isActive ? "สลับเป็นปิด" : "สลับเป็นเปิด"}
                    </button>
                    <div className="flex items-center gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => openEditTypeModal(item)}
                        className="h-6 w-6 text-slate-600 hover:text-amber-700"
                        title="แก้ไข"
                      >
                        <Edit3 size={12} />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleDeleteType(item.id, item.titleTh)}
                        className="h-6 w-6 text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                        title="ลบ"
                      >
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Claims List Header */}
      <div className="space-y-2">
        <div className="flex items-center justify-between px-1">
          <h3 className="text-xs sm:text-sm font-black text-slate-800 flex items-center gap-1.5">
            <span>🎁</span>{" "}
            {isMy
              ? `လျှောက်ထားမှု မှတ်တမ်းများ (${claims.length})`
              : `รายการคำขอเบิกสวัสดิการ (${claims.length})`}
          </h3>
          <span className="text-[10px] text-slate-400 font-medium">
            {isManager ? "เรียงลำดับคำขอล่าสุด" : "เฉพาะคำขอของคุณ"}
          </span>
        </div>

        {claims.length === 0 ? (
          <div className="p-6 text-center rounded-2xl border border-dashed border-slate-200 bg-white/50">
            <p className="text-xs font-bold text-slate-400">
              {isMy ? "သက်သာချောင်ချိရေး မှတ်တမ်း မရှိသေးပါ" : "ยังไม่มีรายการคำขอสวัสดิการ"}
            </p>
          </div>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {claims.map((claim: any) => {
              const isPending = claim.status === "pending";
              const isApproved = claim.status === "approved";
              const matchedType = availableTypes.find(
                (type) => type.titleTh === claim.welfareType || type.titleMy === claim.welfareType
              );
              const claimTypeLabel = isMy && matchedType?.titleMy
                ? matchedType.titleMy
                : (matchedType?.titleTh || claim.welfareType);
              return (
                <div
                  key={claim.id}
                  className={`p-3 rounded-2xl border transition-all ${
                    isApproved
                      ? "border-emerald-200 bg-emerald-50/20"
                      : isPending
                      ? "border-amber-200 bg-white"
                      : "border-rose-200 bg-rose-50/20"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="font-mono text-[9px] font-bold text-slate-400">
                          {claim.claimNo}
                        </span>
                        <Badge
                          variant={
                            isApproved ? "default" : isPending ? "outline" : "destructive"
                          }
                          className="text-[9px] font-bold py-0 h-4"
                        >
                          {isApproved
                            ? isMy
                              ? "✅ အတည်ပြုပြီး"
                              : "✅ อนุมัติแล้ว"
                            : isPending
                            ? isMy
                              ? "⏳ စိစစ်ဆဲ"
                              : "⏳ รอพิจารณา"
                            : isMy
                            ? "❌ ငြင်းပယ်သည်"
                            : "❌ ไม่อนุมัติ"}
                        </Badge>
                      </div>
                      <h4 className="text-xs sm:text-sm font-black text-slate-900 truncate">
                        {claimTypeLabel}
                      </h4>
                      {isManager && (
                        <p className="text-[11px] font-bold text-slate-700 mt-0.5 truncate">
                          {claim.displayName}
                        </p>
                      )}
                      {claim.reason && (
                        <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">
                          {claim.reason}
                        </p>
                      )}
                    </div>

                    <div className="text-right shrink-0">
                      <p className="text-sm sm:text-base font-black text-slate-900 font-numeric">
                        {Number(claim.amount).toLocaleString()} ฿
                      </p>
                    </div>
                  </div>

                  {claim.decisionNote && (
                    <div className="mt-2 text-[11px] bg-slate-50 p-1.5 rounded-lg border border-slate-100">
                      <span className="font-bold text-slate-600">ผลการพิจารณา: </span>
                      <span className="text-slate-700">{claim.decisionNote}</span>
                    </div>
                  )}

                  {/* Manager Approval Actions */}
                  {isManager && isPending && (
                    <div className="mt-2.5 flex gap-2 pt-2 border-t border-slate-100">
                      <Button
                        size="sm"
                        onClick={() =>
                          idToken &&
                          decideWelfare.mutate({
                            idToken,
                            claimId: claim.id,
                            status: "approved",
                          })
                        }
                        disabled={decideWelfare.isPending}
                        className="flex-1 rounded-xl text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white h-7.5"
                      >
                        <CheckCircle2 size={13} className="mr-1" />
                        อนุมัติ
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          idToken &&
                          decideWelfare.mutate({
                            idToken,
                            claimId: claim.id,
                            status: "rejected",
                          })
                        }
                        disabled={decideWelfare.isPending}
                        className="rounded-xl text-xs font-bold text-rose-600 border-rose-200 hover:bg-rose-50 h-7.5 px-3"
                      >
                        <XCircle size={13} className="mr-1" />
                        ไม่อนุมัติ
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* POPUP MODAL: Employee Welfare Request Form */}
      <Dialog open={showClaimModal} onOpenChange={setShowClaimModal}>
        <DialogContent className="max-w-md rounded-2xl p-4 sm:p-5">
          <DialogHeader className="pb-1 pr-8">
            <DialogTitle className="text-base font-black text-amber-950 flex items-center gap-2">
              <Gift size={18} className="text-amber-600" />
              {isMy ? "သက်သာချောင်ချိရေး အသစ် လျှောက်ထားခြင်း" : "ยื่นคำขอเบิกสวัสดิการ"}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {isMy
                ? "HR နှင့် မန်နေဂျာမှ စစ်ဆေးအတည်ပြုပြီး LINE ဖြင့် အကြောင်းကြားပါမည်"
                : "ฝ่ายบุคคลและผู้บริหารจะพิจารณาและแจ้งผลให้ทราบทาง LINE"}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleClaimSubmit} className="space-y-3 pt-1">
            <div>
              <label className="block text-xs font-black text-slate-700 mb-1">
                {isMy ? "အမျိုးအစား ရွေးချယ်ပါ *" : "เลือกประเภทสวัสดิการ *"}
              </label>
              <select
                value={selectedTypeId}
                onChange={(e) => setSelectedTypeId(Number(e.target.value))}
                required
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs sm:text-sm font-bold text-slate-800 outline-none focus:border-amber-500"
              >
                <option value="">{isMy ? "-- ရွေးချယ်ပါ --" : "-- เลือกประเภทสวัสดิการ --"}</option>
                {availableTypes
                  .filter((t) => t.isActive)
                  .map((type) => (
                    <option key={type.id} value={type.id}>
                      {isMy && type.titleMy ? `${type.titleMy} (${type.titleTh})` : type.titleTh}
                      {type.maxAmount > 0 ? ` [สูงสุด ${type.maxAmount.toLocaleString()} ฿]` : ""}
                    </option>
                  ))}
              </select>

              {selectedTypeItem && (
                <div className="mt-1.5 flex items-center justify-between text-[11px] bg-amber-50 p-2 rounded-lg border border-amber-200 text-amber-900">
                  <span className="font-semibold flex items-center gap-1">
                    <Info size={13} className="text-amber-600 shrink-0" />
                    {selectedTypeItem.description || "สวัสดิการตามระเบียบองค์กร"}
                  </span>
                  {selectedTypeItem.maxAmount > 0 && (
                    <span className="font-bold shrink-0">
                      สูงสุด {selectedTypeItem.maxAmount.toLocaleString()} ฿
                    </span>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-black text-slate-700 mb-1">
                {isMy ? "ထုတ်ယူလိုသောငွေ (ဘတ်) *" : "จำนวนเงินที่ขอเบิก (บาท) *"}
              </label>
              <input
                type="number"
                step="1"
                min="1"
                max={selectedTypeItem?.maxAmount && selectedTypeItem.maxAmount > 0 ? selectedTypeItem.maxAmount : undefined}
                required
                placeholder={isMy ? "ဥပမာ- 500 သို့မဟုတ် 1000" : "เช่น 500 หรือ 1000"}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-800 outline-none focus:border-amber-500"
              />
            </div>

            <div>
              <label className="block text-xs font-black text-slate-700 mb-1">
                {isMy ? "အကြောင်းပြချက် / အသေးစိတ်" : "เหตุผล / รายละเอียดความจำเป็น"}
              </label>
              <textarea
                rows={2}
                placeholder={isMy ? "အသေးစိတ် ရိုက်ထည့်ပါ..." : "ระบุรายละเอียดเพิ่มเติม..."}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs sm:text-sm font-medium text-slate-800 outline-none focus:border-amber-500"
              />
            </div>

            <DialogFooter className="pt-2 flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowClaimModal(false)}
                className="rounded-xl text-xs font-bold"
              >
                {isMy ? "မလုပ်တော့ပါ" : "ยกเลิก"}
              </Button>
              <Button
                type="submit"
                disabled={requestWelfare.isPending}
                size="sm"
                className="rounded-xl text-xs font-bold bg-amber-600 hover:bg-amber-700 text-white shadow-xs"
              >
                <Send size={13} className="mr-1.5" />
                {requestWelfare.isPending
                  ? isMy
                    ? "တင်သွင်းနေပါသည်..."
                    : "กำลังยื่น..."
                  : isMy
                  ? "လျှောက်လွှာတင်မည်"
                  : "ส่งคำขอเบิกสวัสดิการ"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* POPUP MODAL: Admin Create / Edit Welfare Type */}
      {isManager && (
        <Dialog open={showTypeModal} onOpenChange={setShowTypeModal}>
          <DialogContent className="max-w-md rounded-2xl p-4 sm:p-5">
            <DialogHeader className="pb-1 pr-8">
              <DialogTitle className="text-base font-black text-slate-900 flex items-center gap-2">
                <Settings size={18} className="text-amber-600" />
                {editingType ? "แก้ไขประเภทสวัสดิการ" : "เพิ่มประเภทสวัสดิการใหม่"}
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500">
                กำหนดประเภทสวัสดิการ วงเงินสูงสุด และคำอธิบายสำหรับให้พนักงานขอเบิก
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSaveType} className="space-y-3 pt-1">
              <div>
                <label className="block text-xs font-black text-slate-700 mb-1">
                  ชื่อสวัสดิการ (ภาษาไทย) *
                </label>
                <input
                  type="text"
                  required
                  placeholder="เช่น ค่ารักษาพยาบาล หรือ ค่าเดินทาง"
                  value={typeForm.titleTh}
                  onChange={(e) => setTypeForm((prev) => ({ ...prev, titleTh: e.target.value }))}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs sm:text-sm font-bold text-slate-800 outline-none focus:border-amber-500"
                />
              </div>

              <div>
                <label className="block text-xs font-black text-slate-700 mb-1">
                  ชื่อสำหรับพนักงานภาษาพม่า (ไม่บังคับ)
                </label>
                <input
                  type="text"
                  placeholder="กรอกคำแปลภาษาพม่าเพื่อแสดงในหน้าพนักงาน"
                  value={typeForm.titleMy}
                  onChange={(e) => setTypeForm((prev) => ({ ...prev, titleMy: e.target.value }))}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs sm:text-sm font-bold text-slate-800 outline-none focus:border-amber-500"
                />
              </div>

              <div>
                <label className="block text-xs font-black text-slate-700 mb-1">
                  วงเงินสูงสุดที่ขอเบิกได้ต่อครั้ง (บาท)
                </label>
                <input
                  type="number"
                  min="0"
                  step="50"
                  placeholder="0 = ไม่จำกัดวงเงิน"
                  value={typeForm.maxAmount}
                  onChange={(e) => setTypeForm((prev) => ({ ...prev, maxAmount: e.target.value }))}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs sm:text-sm font-bold text-slate-800 outline-none focus:border-amber-500"
                />
                <p className="text-[10px] text-slate-400 mt-0.5">ระบุ 0 หากไม่มีการจำกัดวงเงินสูงสุด</p>
              </div>

              <div>
                <label className="block text-xs font-black text-slate-700 mb-1">
                  คำอธิบายหรือเงื่อนไขการเบิก
                </label>
                <textarea
                  rows={2}
                  placeholder="เช่น แนบใบเสร็จค่ารักษา, เบิกได้ไม่เกินปีละ 1 ครั้ง"
                  value={typeForm.description}
                  onChange={(e) => setTypeForm((prev) => ({ ...prev, description: e.target.value }))}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs sm:text-sm font-medium text-slate-800 outline-none focus:border-amber-500"
                />
              </div>

              <DialogFooter className="pt-2 flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowTypeModal(false)}
                  className="rounded-xl text-xs font-bold"
                >
                  ยกเลิก
                </Button>
                <Button
                  type="submit"
                  disabled={createType.isPending || updateType.isPending}
                  size="sm"
                  className="rounded-xl text-xs font-bold bg-amber-600 hover:bg-amber-700 text-white"
                >
                  {createType.isPending || updateType.isPending ? "กำลังบันทึก..." : "บันทึกข้อมูล"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
